import type { HtmlParserPlugin } from '~/plugins/dom-parser'
import { MARKING_EXCLUDED_ATTRIBUTE } from '#shared/utils/markings/rules'
import { icon } from '@fortawesome/fontawesome-svg-core'
import { faXmark } from '@fortawesome/pro-light-svg-icons'

const FOOTNOTE_CONTENT_CLASS = 'footnote__content'
const FOOTNOTE_ID_PREFIX = 'fn'
const MAX_FOOTNOTE_DOM_ID_LENGTH = 50
const MAX_FOOTNOTE_ID_SUFFIX_LENGTH = MAX_FOOTNOTE_DOM_ID_LENGTH - FOOTNOTE_ID_PREFIX.length
const FOOTNOTE_DOM_ID_PATTERN = new RegExp(`^${FOOTNOTE_ID_PREFIX}[A-Za-z0-9_*-]{1,${MAX_FOOTNOTE_ID_SUFFIX_LENGTH}}$`)

export function normalizeFootnoteReferenceId(rawId: string | undefined): string | undefined {
  const value = rawId?.trim()
  if (!value) {
    return undefined
  }

  if (value.length > MAX_FOOTNOTE_DOM_ID_LENGTH) {
    return undefined
  }

  const normalized = value.startsWith('ft')
    ? (() => {
        const suffix = value.slice(2)
        return suffix ? `${FOOTNOTE_ID_PREFIX}${suffix}` : undefined
      })()
    : /^\d+$/.test(value)
      ? `${FOOTNOTE_ID_PREFIX}${value}`
      : value.startsWith(FOOTNOTE_ID_PREFIX)
        ? value
        : `${FOOTNOTE_ID_PREFIX}${value}`

  if (!normalized || normalized.length > MAX_FOOTNOTE_DOM_ID_LENGTH) {
    return undefined
  }

  return normalized
}

export function sanitizeFootnoteDomId(rawId: string | undefined): string | undefined {
  const normalized = normalizeFootnoteReferenceId(rawId)
  if (!normalized) {
    return undefined
  }

  return FOOTNOTE_DOM_ID_PATTERN.test(normalized) ? normalized : undefined
}

/**
 * HTML parser plugin for setting up footnote containers in the literature document.
 *
 * This includes creating placeholder elements for inserting footnote content into later, as well as transforming footnote references into buttons for interactivity.
 *
 * @param doc - The document to process and set up footnote containers in.
 * @returns The modified document with footnote containers set up.
 */
export const setupFootnoteContainers: HtmlParserPlugin = (doc) => {
  const footnoteLinks = doc.body.querySelectorAll('a[epub\\:type="noteref"], a[href*="#fn"]')

  for (const link of footnoteLinks) {
    const supElement = link.closest('sup') || link.querySelector('sup')

    const footnoteNumber = supElement?.textContent?.trim()
      || link.textContent?.trim()
      || ''

    const href = link.getAttribute('href') || ''

    const footnoteId = sanitizeFootnoteDomId(href.split('#').pop())

    if (!footnoteId) {
      continue
    }

    const outer = supElement
      ? (supElement.parentElement === link ? link : supElement)
      : link
    const newSup = createFootnoteSup(doc, footnoteId, footnoteNumber)

    if (supElement && outer === link && supElement.parentElement === link) {
      supElement.remove()
    }

    outer.replaceWith(newSup)

    insertContainer(doc, footnoteId, newSup)
  }

  return doc
}

/**
 * HTML parser plugin for setting up footnote containers in the journal document.
 *
 * @param doc - The document to process and set up footnote containers in.
 * @returns The modified document with footnote containers set up.
 */
export const setupJournalFootnoteContainers: HtmlParserPlugin = (doc) => {
  const footnoteSups = doc.body.querySelectorAll('sup[data-footnote-id]')

  for (const sup of footnoteSups) {
    const element = sup as HTMLElement
    const footnoteId = element.dataset.footnoteId

    if (!footnoteId) {
      continue
    }

    element.setAttribute(MARKING_EXCLUDED_ATTRIBUTE, '')
    insertContainer(doc, footnoteId, element)
  }

  return doc
}

/**
 * Create a superscript element for a footnote reference which contains a button that can be clicked to toggle the footnote content.
 *
 * @param doc - The document to create elements in.
 * @param footnoteId - The ID of the footnote to reference.
 * @param content - The content to display in the superscript (usually the footnote number).
 * @returns The created superscript element containing the footnote button.
 */
function createFootnoteSup(doc: Document, footnoteId: string, content: string) {
  const btn = doc.createElement('button')
  btn.setAttribute('data-footnote-id', footnoteId)
  btn.setAttribute(MARKING_EXCLUDED_ATTRIBUTE, '')
  btn.setAttribute('aria-expanded', 'false')
  btn.setAttribute('aria-controls', footnoteId)
  btn.classList.add('underline', 'text-caption', 'pl-4xs', 'cursor-pointer')
  btn.innerHTML = content

  const sup = doc.createElement('sup')
  sup.appendChild(btn)
  return sup
}

/**
 * HTML parser plugin for inserting footnote content into the document (i.e. opening and closing footnotes).
 *
 * @param doc - The document to process and insert footnote content into.
 * @param ctx - The context containing footnote content to insert.
 * @returns The modified document with footnote content inserted.
 */
export const insertFootnotes: HtmlParserPlugin = (doc, ctx) => {
  const containers = doc?.querySelectorAll(`.${FOOTNOTE_CONTENT_CLASS}`)
  if (containers && ctx?.content) {
    for (const footnote of ctx.content) {
      const content = footnote.body.firstChild as HTMLElement | null
      if (!content) {
        continue
      }

      const footnoteId = content.id

      for (const container of containers) {
        if ((container as HTMLElement).id === footnoteId) {
          container.removeAttribute('hidden')
          container.innerHTML = content.innerHTML
        }
      }
    }
  }

  return doc
}

/**
 * HTML parser plugin for manipulating raw footnote content in an epub into a more suitable format.
 *
 * Primarily so that some of the footnote logic can be reused for other content types.
 *
 * @param doc - The document to manipulate footnote content in.
 * @returns The modified document with footnote content created.
 */
export const createFootnoteContent: HtmlParserPlugin = (doc) => {
  const pTag = doc.querySelector('*[epub\\:type="footnote"], *[epub\\:type="footnotes"]')
  const aTag = pTag?.querySelector('a[href]')

  if (!aTag || !pTag) {
    return doc
  }

  const div = doc.createElement('div')
  const footnoteId = sanitizeFootnoteDomId(pTag.id)
  if (!footnoteId) {
    return doc
  }

  div.id = footnoteId

  const top = createFootnoteHeader(doc, {
    tag: 'span',
    id: div.id,
    labelContent: aTag.textContent,
  })

  let aTagWrapper = aTag

  while (
    aTagWrapper.parentElement !== pTag
    && aTagWrapper.parentElement?.childNodes.length === 1
  ) {
    aTagWrapper = aTagWrapper.parentElement
  }

  aTagWrapper.remove()

  const text = createFootnoteText(doc, pTag)

  div.appendChild(top)
  div.appendChild(text)

  pTag.replaceWith(div)

  return doc
}

export const createJournalFootnoteContent: HtmlParserPlugin = (doc) => {
  const divTag = doc.querySelector('div')
  const idLabel = divTag?.querySelector('label')

  if (!idLabel || !divTag) {
    return doc
  }

  const top = createFootnoteHeader(doc, {
    tag: 'span',
    id: divTag.id,
    labelContent: idLabel.textContent,
  })

  const content = divTag.querySelector('p')

  if (!content) {
    return doc
  }

  const text = createFootnoteText(doc, content)

  divTag.removeChild(idLabel)
  divTag.removeChild(content)
  divTag.appendChild(top)
  divTag.appendChild(text)

  return doc
}

interface CreateFootnoteTopOptions {
  tag: keyof HTMLElementTagNameMap
  id: string
  labelContent?: string
}

/**
 * Create the header part of the footnote, including the label and close button.
 *
 * @param doc - The document to create elements in.
 * @param options - Options for creating the footnote header such as the footnote ID.
 */
export function createFootnoteHeader(doc: Document, options: CreateFootnoteTopOptions) {
  const header = doc.createElement(options.tag)
  header.classList.add('flex', 'justify-between', 'items-center')

  const label = doc.createElement('span')
  label.className = 'footnote__label'
  label.textContent = ['Fotnote', options.labelContent ?? options.id].filter(Boolean).join(' ')
  header.appendChild(label)

  const closeButton = doc.createElement('button')
  closeButton.dataset.footnoteId = options.id
  closeButton.classList.add('footnote__close-btn')
  closeButton.innerHTML = icon(faXmark).html[0] ?? 'X'
  closeButton.ariaLabel = 'Lukk fotnote'
  header.appendChild(closeButton)

  return header
}

/**
 * Create the text content part of the footnote by wrapping the given content's child nodes in a span.
 *
 * @param doc - The document to create elements in.
 * @param content - The content element whose child nodes will be wrapped.
 */
export function createFootnoteText(doc: Document, content: Element) {
  const text = doc.createElement('span')
  text.className = 'footnote__text'

  const children = Array.from(content.childNodes)

  for (const child of children) {
    text.appendChild(child)
  }

  return text
}

export function getFootnoteIdFromEvent(event: Event): string | undefined {
  const target = event.target

  if (target instanceof HTMLElement) {
    return target.dataset.footnoteId
      ?? target.parentElement?.dataset.footnoteId
  }

  if (target instanceof Element) {
    return target.parentElement?.dataset.footnoteId
  }

  return undefined
}

function insertContainer(doc: Document, footnoteId: string, element: HTMLElement) {
  const container = createContainerElement(doc, footnoteId)

  const insertionPoint = footnoteInsertionPoint(element)

  insertionPoint.insertAdjacentElement('afterend', container)
}

function footnoteInsertionPoint(sup: HTMLElement) {
  let insertionPoint = sup
  let sibling = sup.nextSibling

  // Check forward only if the next, direct node is a <sup>, and not a text node.
  while (sibling) {
    if (sibling.nodeType === Node.TEXT_NODE) {
      if (sibling.textContent?.trim() !== '') {
        break
      }
    }
    else if (
      sibling.nodeType === Node.ELEMENT_NODE
      && (sibling as Element).tagName.toLowerCase() === 'sup'
    ) {
      insertionPoint = sibling as HTMLElement
    }
    else {
      break
    }

    sibling = sibling.nextSibling
  }
  return insertionPoint
}

function createContainerElement(doc: Document, footnoteId: string) {
  const container = doc.createElement('span')
  container.className = FOOTNOTE_CONTENT_CLASS
  container.id = footnoteId
  container.setAttribute('hidden', 'true')
  container.setAttribute('data-footnote-id', footnoteId)

  return container
}

export type FootnotesState = Record<string, {
  open: boolean
  content: Document
}>
